// src/pages/admin/ManageAdminsPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProtectedRoute from '../../../components/auth/ProtectedRoute';
import { API_BASE_URL_ELECTIONS } from '../../../utils/constants';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import ElectionProtectedRoute from '../../../components/auth/ElectionProtectedRoute';

const ManageAdminsPage = () => {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [selectedSchools, setSelectedSchools] = useState([]);

  useEffect(() => {
    checkAuth();
    fetchAdmins();
    fetchSchools();
  }, []);

  const checkAuth = () => {
    const userData = localStorage.getItem('election_user');
    if (!userData) {
      navigate('/admin/election-login');
      return;
    }
    const user = JSON.parse(userData);
    if (user.role !== 'SUPER_ADMIN') {
      navigate('/admin/election-dashboard');
    }
  };

  const fetchAdmins = async () => {
    try {
      const token = localStorage.getItem('election_token');
      const response = await fetch(`${API_BASE_URL_ELECTIONS}/auth/admins-with-schools`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setAdmins(data.data);
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSchools = async () => {
    try {
      const response = await fetch(`${API_BASE_URL_ELECTIONS}/schools`);
      const data = await response.json();
      if (data.success) {
        setSchools(data.data);
      }
    } catch (error) {
      console.error('Error fetching schools:', error);
    }
  };

  const handleApprove = async (adminId) => {
    try {
      const token = localStorage.getItem('election_token');
      const response = await fetch(`${API_BASE_URL_ELECTIONS}/auth/admins/${adminId}/approve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        fetchAdmins();
      }
    } catch (error) {
      console.error('Error approving admin:', error);
    }
  };

  const handleDelete = async (adminId) => {
    if (!ConfirmDialog('Are you sure you want to delete this admin?')) return;

    try {
      const token = localStorage.getItem('election_token');
      const response = await fetch(`${API_BASE_URL_ELECTIONS}/auth/admins/${adminId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        fetchAdmins();
      }
    } catch (error) {
      console.error('Error deleting admin:', error);
    }
  };

  const openAssignModal = (admin) => {
    setSelectedAdmin(admin);
    const assignedSchoolIds = admin.assignedSchools?.map(as => as.school.id) || [];
    setSelectedSchools(assignedSchoolIds);
    setShowAssignModal(true);
  };

  const handleSchoolToggle = (schoolId) => {
    if (selectedSchools.includes(schoolId)) {
      setSelectedSchools(selectedSchools.filter(id => id !== schoolId));
    } else {
      setSelectedSchools([...selectedSchools, schoolId]);
    }
  };

  const handleAssignSchools = async () => {
    try {
      const token = localStorage.getItem('election_token');
      const response = await fetch(`${API_BASE_URL_ELECTIONS}/auth/admins/${selectedAdmin.id}/assign-schools`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ schoolIds: selectedSchools })
      });

      const data = await response.json();
      if (data.success) {
        setShowAssignModal(false);
        fetchAdmins();
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error('Error assigning schools:', error);
      alert('Failed to assign schools');
    }
  };

  if (loading) {
    return (
      <ElectionProtectedRoute>
        <div className="flex items-center justify-center min-h-screen bg-black">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-600 mx-auto"></div>
            <p className="mt-4 text-gray-400 font-semibold">Loading Admins...</p>
          </div>
        </div>
      </ElectionProtectedRoute>
    );
  }

  return (
    <ElectionProtectedRoute>
      <div className="min-h-screen bg-black">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-900 via-black to-gray-900 border-b border-red-900/30 shadow-xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <button
                  onClick={() => navigate('/admin/election-dashboard')}
                  className="flex items-center gap-2 text-red-400 hover:text-red-300 mb-3 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to Dashboard
                </button>
                <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                  <span className="text-red-500">👥</span>
                  Manage Admins
                </h1>
                <p className="text-gray-400 mt-1">Approve admins and assign schools</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Admins Table */}
          <div className="bg-gradient-to-br from-gray-900 to-black border border-red-900/30 rounded-xl shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-800">
                <thead className="bg-gray-800/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                      Admin
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                      Assigned Schools
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {admins.map((admin) => (
                    <tr key={admin.id} className="hover:bg-gray-800/30 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-semibold text-white">{admin.fullName}</div>
                          <div className="text-sm text-gray-400">{admin.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                          admin.role === 'SUPER_ADMIN' 
                            ? 'bg-red-900/30 text-red-400 border-red-500/50' 
                            : 'bg-gray-800 text-gray-300 border-gray-600'
                        }`}>
                          {admin.role === 'SUPER_ADMIN' ? '👑 Super Admin' : 'School Admin'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                          admin.isActive 
                            ? 'bg-green-900/30 text-green-400 border-green-500/50' 
                            : 'bg-yellow-900/30 text-yellow-400 border-yellow-500/50'
                        }`}>
                          {admin.isActive ? '✓ Active' : '⏳ Pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {admin.role === 'SUPER_ADMIN' ? (
                          <span className="text-sm text-gray-400 italic">All Schools</span>
                        ) : admin.assignedSchools?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {admin.assignedSchools.slice(0, 2).map(as => (
                              <span key={as.id} className="px-2 py-1 bg-red-900/20 text-red-400 border border-red-500/30 rounded text-xs">
                                {as.school.shortName || as.school.name}
                              </span>
                            ))}
                            {admin.assignedSchools.length > 2 && (
                              <span className="px-2 py-1 bg-gray-800 text-gray-400 border border-gray-700 rounded text-xs">
                                +{admin.assignedSchools.length - 2} more
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">None</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-2">
                          {!admin.isActive && (
                            <button
                              onClick={() => handleApprove(admin.id)}
                              className="px-3 py-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded font-semibold shadow-lg shadow-green-600/20 transition-all duration-300 transform hover:scale-105"
                            >
                              Approve
                            </button>
                          )}
                          {admin.role !== 'SUPER_ADMIN' && (
                            <button
                              onClick={() => openAssignModal(admin)}
                              className="px-3 py-1 bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-white rounded font-semibold transition-all duration-300 border border-gray-600 hover:border-red-500/50"
                            >
                              Assign
                            </button>
                          )}
                          {admin.role !== 'SUPER_ADMIN' && (
                            <button
                              onClick={() => handleDelete(admin.id)}
                              className="px-3 py-1 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded font-semibold shadow-lg shadow-red-600/20 transition-all duration-300 transform hover:scale-105"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {admins.length === 0 && (
                <div className="text-center py-16">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-red-900/20 rounded-full mb-4 border border-red-500/30">
                    <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">No admins found</h3>
                  <p className="text-gray-400">Admins will appear here once they register</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Assign Schools Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-red-900/30 rounded-2xl max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto shadow-2xl relative">
            {/* Decorative border glow */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl opacity-20 blur"></div>
            
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                  <span className="text-red-500">🏫</span>
                  Assign Schools
                </h2>
                <button
                  onClick={() => setShowAssignModal(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-gray-400 mb-6">
                Select schools for <span className="text-red-400 font-semibold">{selectedAdmin?.fullName}</span> to manage
              </p>

              <div className="space-y-3 mb-6 max-h-96 overflow-y-auto pr-2">
                {schools.map((school) => (
                  <label
                    key={school.id}
                    className="flex items-center gap-3 p-4 bg-gray-800/50 border border-gray-700 rounded-lg hover:bg-gray-800 hover:border-red-500/50 cursor-pointer transition-all duration-300 group"
                  >
                    <input
                      type="checkbox"
                      checked={selectedSchools.includes(school.id)}
                      onChange={() => handleSchoolToggle(school.id)}
                      className="w-5 h-5 text-red-600 bg-gray-700 border-gray-600 rounded focus:ring-2 focus:ring-red-500"
                    />
                    <div className="flex items-center gap-3 flex-1">
                      {school.logo ? (
                        <img src={school.logo} alt={school.name} className="w-12 h-12 rounded-lg object-cover border-2 border-red-500/30" />
                      ) : (
                        <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 rounded-lg flex items-center justify-center text-white font-bold border-2 border-red-500/30">
                          {school.shortName || school.name.charAt(0)}
                        </div>
                      )}
                      <div>
                        <div className="font-semibold text-white group-hover:text-red-400 transition-colors">{school.name}</div>
                        {school.shortName && (
                          <div className="text-sm text-gray-400">{school.shortName}</div>
                        )}
                      </div>
                    </div>
                    {selectedSchools.includes(school.id) && (
                      <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </label>
                ))}
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-800">
                <button
                  onClick={() => setShowAssignModal(false)}
                  className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 hover:bg-gray-700 hover:border-gray-600 text-white rounded-lg font-semibold transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignSchools}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg font-semibold shadow-lg shadow-red-600/30 transition-all duration-300 transform hover:scale-105"
                >
                  Assign {selectedSchools.length} School{selectedSchools.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ElectionProtectedRoute>
  );
};

export default ManageAdminsPage;